import { makeAutoObservable } from 'mobx'
import { type IDropdownItem, type ITooltipProps } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { AddPaymentMethodStore } from 'entities/Billing/ui/AddPaymentMethodModal'
import { type Source } from 'entities/Billing/model/Source'
import type { BillingStore } from 'entities/Billing'

type IDropdownCardsStoreProps = {
  activeId?: string
  hideId?: string
  labelText?: string
  labelTooltip?: ITooltipProps
  withoutDefaultPresetCard?: boolean
  enableAch?: boolean
  isFranchise?: boolean
  defaultPaymentMethodId?: string | null
}

export class DropdownCardsStore {
  private _activeId: string | null = null
  private _hideId: string | null = null
  private _withoutDefaultPresetCard = false
  private _labelText = 'Payment method'
  private _labelTooltip: ITooltipProps | null = null
  private _enableAch = false
  private _paymentError = ''
  private _isFranchise = false
  private _defaultPaymentMethodId: string | null = null

  constructor(private _billingStore: BillingStore, props?: IDropdownCardsStoreProps) {
    makeAutoObservable(this)

    if (props?.activeId) {
      this._activeId = props.activeId
    }
    if (props?.hideId) {
      this._hideId = props.hideId
    }
    if (props?.labelText) {
      this._labelText = props.labelText
    }
    if (props?.labelTooltip) {
      this._labelTooltip = props.labelTooltip
    }
    if (props?.withoutDefaultPresetCard) {
      this._withoutDefaultPresetCard = props.withoutDefaultPresetCard
    }
    if (props?.enableAch) {
      this._enableAch = props.enableAch
    }
    if (props?.isFranchise) {
      this._isFranchise = props.isFranchise
    }

    if (props?.defaultPaymentMethodId) {
      this._defaultPaymentMethodId = props.defaultPaymentMethodId
    }
  }

  setFirstActive = () => {
    if (!this.sources.length) return
    let franchisePrimarySource = null

    if (this._isFranchise) {
      franchisePrimarySource = this.sources.find((source) => source.franchise_primary)
    }

    const primarySource = this.sources.find((source) => source.primary)

    const targetSource =
      this._isFranchise && franchisePrimarySource
        ? franchisePrimarySource
        : primarySource || this.sources[0]

    if (targetSource) {
      this.setActiveId(targetSource.id)
    }

    if (this.defaultPaymentMethod) {
      this.setActiveId(this.defaultPaymentMethod.id)
    }
  }

  loadSources = async () => {
    try {
      if (this._billingStore.sourcesLoading) return

      await this._billingStore.fetchBillingSources()

      if (this._withoutDefaultPresetCard) return
      if (this.sources.length) {
        this.setFirstActive()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  onChange = (item: IDropdownItem) => {
    this.setActiveId(item.id as string)
    this.clearPaymentError()
  }

  setActiveId = (value: typeof this._activeId) => {
    this._activeId = value
  }

  onAddPaymentMethod = () => {
    new AddPaymentMethodStore(
      (source) => {
        this._activeId = source.id
        this.clearPaymentError()
      },
      this._billingStore,
      ZIndex.OVERLAY_TOP_120
    )
  }

  setHideId = (value: typeof this._hideId) => {
    if (this._activeId === value) {
      this._activeId = null
    }
    this._hideId = value
  }

  setPaymentError = (value: string) => {
    this._paymentError = value
  }

  clearPaymentError = () => {
    this._paymentError = ''
  }

  get activeItem(): Source | null {
    return (this._activeId && this._billingStore.getSources(this._activeId)) || null
  }

  get sources() {
    if (this._enableAch) {
      return this._billingStore.sources.filter((source) => source.id !== this._hideId)
    }

    return this._billingStore.sourcesSimple.filter((source) => source.id !== this._hideId)
  }

  get activeId() {
    return this._activeId
  }

  get labelText() {
    return this._labelText
  }

  get labelTooltip() {
    return this._labelTooltip
  }

  get paymentError() {
    return this._paymentError
  }

  get defaultPaymentMethod() {
    return this._defaultPaymentMethodId
      ? this._billingStore.getSources(this._defaultPaymentMethodId)
      : null
  }

  get isFixedACHPayment() {
    return (
      (this._isFranchise && this.activeItem?.object === 'payment_method') ||
      (this.defaultPaymentMethod && this.defaultPaymentMethod.object === 'payment_method')
    )
  }
}
